// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import classnames from '@ta/common.classnames';
import styles from './TripDetailLayout.less';

type ExternalProps = {|
  header: React.Node,
  content: React.Node,
  map: React.Node,
  // If true, the trip item list will be a scrollable container
  containerScroll?: boolean,
|};

type Refs = {|
  backdropCover: HTMLDivElement | null,
  overlayCover: HTMLDivElement | null,
  mapContainer: HTMLDivElement | null,
|};

type Props = {|
  ...ExternalProps,
  forwardedRef: { current: null | Refs } | ((null | Refs) => mixed),
|};

/** A re-usable styled layout of the trip page */
const TripDetailLayout = ({
  header,
  content,
  map,
  containerScroll = false,
  forwardedRef,
}: Props) => {
  const proxyRef = React.useRef<Refs>({
    backdropCover: null,
    overlayCover: null,
    mapContainer: null,
  });
  // Refs set on DOM nodes need to be done synchronously
  React.useLayoutEffect(() => {
    if (typeof forwardedRef === 'function') {
      forwardedRef(proxyRef.current);
    } else if (typeof forwardedRef === 'object' && forwardedRef !== null) {
      /* eslint-disable no-param-reassign */
      forwardedRef.current = proxyRef.current;
      /* eslint-enable no-param-reassign */
    }
  }, [forwardedRef, map]);
  return (
    <div
      ref={el => {
        proxyRef.current.backdropCover = el;
      }}
      className={classnames({ [styles.backdrop_cover]: containerScroll })}
    >
      <div
        className={classnames('ui_columns', 'is-gapless', styles.trip_page_columns, {
          [styles.container_scroll]: containerScroll,
        })}
      >
        <div
          className={classnames('ui_column', styles.left_column, {
            [styles.container_scroll]: containerScroll,
            scrollingModal: containerScroll,
          })}
          ref={el => {
            proxyRef.current.overlayCover = el;
          }}
        >
          {header}
          {content}
        </div>
        {map && (
          <div
            id="map_container_column"
            className={`ui_column is-hidden-mobile ${styles.right_column}`}
            ref={el => {
              proxyRef.current.mapContainer = el;
            }}
          >
            <div className={styles.right_column_inner}>{map}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConnectedTripDetailLayout = connect<Props, Props, _, _, _, _>((state, { map }) =>
  // Suppress map on MW
  ({ map: state.meta.device.viewportCategory === 'MOBILE' ? null : map }),
)(TripDetailLayout);
const ForwardedTripDetailLayout = (props, ref) => (
  <ConnectedTripDetailLayout {...props} forwardedRef={ref} />
);
export default React.forwardRef<ExternalProps, Refs>(ForwardedTripDetailLayout);
